import { ApprovalableTypeEnum } from 'api-hooks/common';
import { ApprovalItemWorkflowEnum } from 'api-hooks/sale/common';
import {
  TravelDocumentSalesModel,
  getTravelDocumentsSalesKey,
  useCreateTravelDocumentSales,
} from 'api-hooks/sale/travel-document';
import { Notification, KIND } from 'baseui/notification';
import { NavigationRoutes } from 'common/constants';
import { queryClient } from 'common/repositories/query-client';
import notification from 'components/common/notification';
import { isDataDraft } from 'hooks/use-approval-action';
import useDialog from 'hooks/use-dialog';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import RequestApprovalModal from 'legacy-modules/approval/approval/components/request-modal';
import { SeparatorVertical } from 'legacy-modules/styles';
import React from 'react';
import { NavigationProps } from 'types';

import { DocumentSaleFormType } from './components/form-type';
import TravelDocumentInvoiceForm from './forms/invoice-form';

export type TravelDocumentSalesCreateParams = {
  defaultValues?: DocumentSaleFormType;
  documentSale?: TravelDocumentSalesModel;
};

export default function TravelDocumentSalesCreate(
  props: NavigationProps<NavigationRoutes.TravelDocumentSalesCreate>,
) {
  const { mutateAsync, error } = useCreateTravelDocumentSales();

  const dialog = useDialog();
  const permission = useGetCrudPermission('document-sales');
  const documentPurchasePermission = useGetCrudPermission('document-purchases');
  const { navigate } = useNavigation();
  const defaultValues = props.navigation.params?.defaultValues;

  return (
    <>
      <TravelDocumentInvoiceForm
        {...props}
        defaultValues={defaultValues}
        renderError={
          !!error &&
          !!error.message && (
            <>
              <Notification
                overrides={{
                  Body: { style: { width: 'auto', wordBreak: 'break-all' } },
                }}
                kind={KIND.negative}
                closeable
              >
                {() => error.message}
              </Notification>
              <SeparatorVertical $margin={8} />
            </>
          )
        }
        onSubmit={async (input, form) => {
          const result = await mutateAsync(input);

          const onDismiss = () => {
            queryClient.refetchQueries([getTravelDocumentsSalesKey()[0]]);

            if (documentPurchasePermission.create) {
              navigate(NavigationRoutes.DocumentPurchasesCreate, {
                documentSale: result.data,
              });
            }
            notification.success({ message: result.message });
          };

          const onDismissCancel = () => {
            queryClient.refetchQueries([getTravelDocumentsSalesKey()[0]]);

            if (permission.view) {
              navigate(NavigationRoutes.TravelDocumentSalesView, {
                id: result.data.id,
              });
            }
          };

          const isDraft = isDataDraft(result.data.status);
          if (isDraft) {
            dialog.showCustom({
              render: (onClose) => (
                <RequestApprovalModal
                  onClose={onClose}
                  data={{
                    approvalableId: result.data.id,
                    approvalableType: ApprovalableTypeEnum.Sale,
                    workflow: ApprovalItemWorkflowEnum.sale,
                  }}
                  onDismissSuccess={onDismissCancel}
                  onDismissCancel={onDismissCancel}
                />
              ),
            });
          } else {
            onDismiss();
          }

          return result.data;
        }}
      />
    </>
  );
}
